/**
 * Live Chat Component Styles
 *
 * Uses SDS (System Design System) tokens for visual consistency
 * with the rest of the WOW application.
 * Buttons are composed using <wow-button> for automatic consistency.
 */

import { css } from "lit";

export const liveChatStyles = css`
  :host {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 200px;
    background: var(--sds-color-background-default-default);
    border-radius: var(--sds-size-radius-400);
    overflow: auto;
  }

  .chat-container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  /* Header */
  .chat-header {
    display: var(--live-chat-header-display, flex);
    flex-direction: column;
    font-weight: var(--sds-typography-weight-semibold);
    font-family: var(--sds-typography-family-title);
    color: var(--sds-color-text-default-default);
  }

  .chat-header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: var(--sds-size-space-800);
    border-bottom: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    margin-bottom: var(--sds-size-space-800);
  }

  .chat-header-title {
    padding: var(--sds-size-space-400) 0;

    border-bottom: 2px solid var(--sds-color-text-default-default);
    color: var(--sds-color-text-default-default);
    text-align: center;

    font-family: var(--sds-typography-family-title);
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%; /* 19.2px */
  }

  .chat-header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .chat-close-button {
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--sds-color-icon-default-secondary);
    padding: 4px;
    border-radius: var(--sds-size-radius-full);
    line-height: 0;
  }

  .chat-close-button:hover {
    color: var(--sds-color-icon-default-default);
    background-color: var(--sds-color-background-default-secondary);
  }

  /* Guidelines Banner */
  .guidelines-banner {
    display: flex;
    align-items: flex-start;
    gap: var(--sds-size-space-300);
    padding: var(--sds-size-space-600);

    background: var(--sds-color-background-default-tertiary);
    border-radius: var(--sds-size-radius-400);
  }

  .banner-content {
    flex: 1;
    min-width: 0;
  }

  .banner-title {
    margin: 0 0 var(--sds-size-space-100) 0;
    font-size: var(--sds-typography-body-size-medium);
    font-weight: var(--sds-typography-weight-medium);
    color: var(--sds-color-text-default-default);
  }

  .banner-subtitle {
    margin: 0;
    font-size: var(--sds-typography-body-size-small);
    font-weight: var(--sds-typography-weight-regular);
    color: var(--sds-color-text-default-secondary);
  }

  .banner-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
    gap: var(--sds-size-space-200);
  }

  .banner-close-button {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    color: var(--sds-color-text-default-secondary);
  }

  .banner-close-button:hover {
    color: var(--sds-color-text-default-default);
  }

  .banner-icon {
    flex-shrink: 0;
    color: var(--sds-color-text-default-secondary);
  }

  /* Loading state */
  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    color: var(--sds-color-text-default-secondary);
  }

  .loading-spinner {
    width: var(--sds-size-icon-small);
    height: var(--sds-size-icon-small);
    border: var(--sds-size-space-100) solid var(--sds-color-text-default-tertiary);
    border-top-color: transparent;
    border-radius: var(--sds-size-radius-full);
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .load-older-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--sds-size-space-400);
  }

  .loading-spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid var(--sds-color-text-default-tertiary);
    border-top-color: transparent;
    border-radius: var(--sds-size-radius-full);
    animation: spin 1s linear infinite;
  }

  /* Error state */
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: var(--sds-size-space-600);
    text-align: center;
    color: var(--sds-color-text-danger-default);
  }

  .error-container wow-button {
    margin-top: var(--sds-size-space-400);
  }

  /* Empty state */
  .empty-container {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    color: var(--sds-color-text-default-secondary);
    padding: var(--sds-size-space-600);
    text-align: center;
    font-size: var(--sds-typography-body-size-medium);
  }

  /* Waiting for event state */
  .waiting-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: var(--sds-size-space-600);
    text-align: center;
  }

  .waiting-icon {
    color: var(--sds-color-text-default-secondary);
    margin-bottom: var(--sds-size-space-400);
  }

  .waiting-message {
    color: var(--sds-color-text-default-secondary);
    font-size: var(--sds-typography-body-size-medium);
    margin: 0;
  }

  /* Messages list */
  .messages-container {
    flex: 1;
    overflow-y: auto;
    padding: var(--sds-size-space-300) 0;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }

  .message {
    display: flex;
    gap: var(--sds-size-space-400);
    padding: var(--sds-size-space-400);
    transition: background-color 0.15s ease;
  }

  .message:hover {
    background: var(--sds-color-background-default-tertiary);
  }

  .message-avatar {
    width: var(--sds-size-icon-medium);
    height: var(--sds-size-icon-medium);
    border-radius: var(--sds-size-radius-full);
    background: var(--sds-color-background-default-tertiary);
    flex-shrink: 0;
    overflow: hidden;
  }

  .message-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--sds-color-white-1000);
    font-size: var(--sds-typography-body-size-small);
    font-weight: var(--sds-typography-weight-semibold);
    border: var(--sds-size-space-100) solid var(--sds-color-border-default-secondary);
    box-sizing: border-box;
  }

  .message-content {
    flex: 1;
    min-width: 0;
  }

  .message-header {
    display: flex;
    align-items: baseline;
    gap: var(--sds-size-space-300);
    margin-bottom: var(--sds-size-space-100);
  }

  .message-author {
    font-family: var(--sds-typography-family-title);
    font-weight: var(--sds-typography-weight-medium);
    color: var(--sds-color-text-default-default);
    font-size: var(--sds-typography-body-size-large);
  }

  .message-time {
    font-size: var(--sds-typography-body-size-small);
    color: var(--sds-color-text-default-secondary);
  }

  .message-text {
    color: var(--sds-color-text-default-default);
    font-size: var(--sds-typography-body-size-medium);
    line-height: 1.4;
    word-break: normal;
    white-space: pre-wrap;
    overflow-wrap: break-word;
  }

  .message-text a {
    color: var(--sds-color-text-default-default);
    text-decoration: underline;
    word-break: break-all;
  }

  /* System message divider */
  .system-message {
    display: flex;
    align-items: center;
    gap: var(--sds-size-space-400);
    padding: var(--sds-size-space-400) var(--sds-size-space-600);
  }

  .system-message-divider {
    flex: 1;
    height: 1px;
    background: var(--sds-color-border-default-default);
  }

  .system-message-text {
    font-size: var(--sds-typography-body-size-small);
    color: var(--sds-color-text-default-secondary);
    white-space: nowrap;
    font-weight: var(--sds-typography-weight-medium);
  }

  /* Input area */
  .input-container {
    flex-shrink: 0;
    padding: var(--sds-size-space-400);
    padding-bottom: calc(var(--sds-size-space-400) + var(--chat-bottom-inset, env(safe-area-inset-bottom, 0px)));
    border-top: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
  }

  /* Emoji picker */
  .emoji-picker {
    display: flex;
    flex-wrap: wrap;
    gap: var(--sds-size-space-150);
    padding: var(--sds-size-space-300);
    background: var(--sds-color-background-default-default);
    border: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    border-radius: var(--sds-size-radius-400);
    margin-bottom: var(--sds-size-space-300);
  }

  .emoji-picker.hidden {
    display: none;
  }

  .emoji-picker wow-button {
    font-size: var(--sds-typography-scale-04);
  }

  /* Login prompt */
  .login-prompt {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--sds-size-space-300);
    padding: var(--sds-size-space-600);
    text-align: center;
    color: var(--sds-color-text-default-secondary);
    font-size: var(--sds-typography-body-size-medium);
  }

  /* Mobile portrait: reduce spacing */
  @media (max-width: 768px) {
    :host {
      min-height: 150px;
      border-radius: 0;
      border-left: none;
      border-right: none;
    }

    .chat-close-button {
      display: flex;
    }

    .chat-header-top {
      padding-left: var(--sds-size-space-400);
      margin-bottom: var(--sds-size-space-400);
      font-size: var(--sds-typography-body-size-large);

      & event-badge {
        margin-right: var(--sds-size-space-150);
      }
    }

    .messages-container {
      padding: var(--sds-size-space-150) 0;
    }

    .message {
      padding: var(--sds-size-space-200) var(--sds-size-space-400);
      gap: var(--sds-size-space-300);
    }

    .message-avatar {
      width: 28px;
      height: 28px;
    }

    .message-author {
      font-size: var(--sds-typography-body-size-medium);
    }

    .message-text {
      font-size: var(--sds-typography-body-size-medium);
    }

    .guidelines-banner {
      margin: 0 var(--sds-size-space-400) var(--sds-size-space-400);
      padding: var(--sds-size-space-400);
    }

    .input-container {
      padding: var(--sds-size-space-300) var(--sds-size-space-400);
    }
  }

  /* Mobile landscape: minimal spacing for maximum content */
  @media (max-height: 500px) and (orientation: landscape) {
    :host {
      min-height: auto;
      border: none;
      border-radius: 0;
      border-left: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    }

    .chat-header-top {
      padding-left: var(--sds-size-space-300);
      margin-bottom: var(--sds-size-space-300);
      font-size: var(--sds-typography-body-size-small);
    }

    .messages-container {
      padding: var(--sds-size-space-100) 0;
    }

    .message {
      padding: var(--sds-size-space-150) var(--sds-size-space-300);
      gap: var(--sds-size-space-200);
    }

    .message-avatar {
      width: var(--sds-size-icon-small);
      height: var(--sds-size-icon-small);
    }

    .message-header {
      margin-bottom: 0;
      gap: var(--sds-size-space-150);
    }

    .message-author {
      font-size: var(--sds-typography-body-size-small);
    }

    .message-time {
      font-size: var(--sds-typography-scale-00);
    }

    .message-text {
      font-size: var(--sds-typography-body-size-small);
      line-height: 1.3;
    }

    .input-container {
      padding: var(--sds-size-space-400);
    }

    .empty-container,
    .waiting-container,
    .error-container {
      padding: var(--sds-size-space-300);
      font-size: var(--sds-typography-body-size-small);
    }

    .login-prompt {
      padding: var(--sds-size-space-300);
      gap: var(--sds-size-space-300);
      font-size: var(--sds-typography-body-size-small);
    }

    .guidelines-banner {
      display: none;
    }
  }
`;
